// src/api/useUpdate.js
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useState, useEffect } from "react";
import { Grid, Button, CircularProgress, Box, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";

const useUpdate = (queryKey, token, redirectPath) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formMessage, setFormMessage] = useState("");
  const [formMessageOpen, setFormMessageOpen] = useState(false);
  const [formMessageSeverity, setFormMessageSeverity] = useState("success");
  const navigate = useNavigate();
  const getResourceKey = () => {
    const parts = String(queryKey || "").split("/").filter(Boolean);
    return parts[parts.length - 1] || queryKey;
  };
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      setLoading(true);
      const headers =
        auth?.buildAuthHeader?.() ||
        (token ? { Authorization: `Token ${token}` } : {});
      try {
        const response = await axios.patch(
          `${process.env.REACT_APP_API_BASE_URL}/${queryKey}/${id}/`,
          data,
          { headers }
        );
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.detail || t("api.failedUpdate"));
      }
    },
    onSuccess: () => {
      setIsSuccess(true);
      setLoading(false);
      setFormMessage(t("api.updateSuccess"));
      setFormMessageSeverity("success");
      setFormMessageOpen(true);
      toast.success(t("api.updateSuccess"), {
        position: "top-right",
        autoClose: 3000,
      });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = String(query.queryKey[0] || "");
          const resource = getResourceKey();
          if (!key || !resource) return false;
          if (key === resource) return true;
          if (resource.endsWith("s") && key === resource.slice(0, -1)) return true;
          if (key.endsWith("s") && resource === key.slice(0, -1)) return true;
          return false;
        },
      });
      if (redirectPath) {
        navigate(redirectPath);
      }
    },
    onError: (error) => {
      setIsSuccess(false);
      setLoading(false);
      setFormMessage(error.message);
      setFormMessageSeverity("error");
      setFormMessageOpen(true);
      toast.error(error.message, {
        position: "top-right",
        autoClose: 3000,
      });
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  useEffect(() => {
    if (formMessageOpen) {
      const timer = setTimeout(() => {
        setFormMessageOpen(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [formMessageOpen]);

  const handleUpdate = (id, data) => {
    updateMutation.mutate({ id, data });
  };

  const FormMessageComponent = () => (
    <Box
      sx={{
        display: formMessageOpen ? "flex" : "none",
        alignItems: "center",
        justifyContent: "space-between",
        bgcolor: formMessageSeverity === "success" ? "green" : "red",
        color: "#fff",
        padding: "10px 15px",
        borderRadius: "4px",
        mt: 2,
        position: "relative",
      }}
    >
      <Box>{formMessage}</Box>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={() => setFormMessageOpen(false)}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  const Buttons = ({ onSubmit }) => (
    <Grid container justifyContent={"end"} mt={2}>
      <Button
        variant="contained"
        color="primary"
        disabled={loading}
        sx={{ m:"0em 1em"}}
      >
        {t("api.cancel")}
      </Button>
      <Button
        variant="contained"
        onClick={onSubmit}
        color="primary"
        disabled={loading}
        startIcon={loading && <CircularProgress size={20} />}
      >
        {loading ? t("api.updating") : t("api.update")}
      </Button>
    </Grid>
  );

  return {
    handleUpdate,
    Buttons,
    loading,
    isSuccess,
  };
};

export default useUpdate;
